import PropTypes from 'prop-types';

import { Box, Button, Typography } from '@mui/material';

import { getEventsCategories } from 'shared/constants';
import { useTransl } from 'shared/lib/i18n';

import BackButtonComponent from '../../Forms/BackButtonComponent';
import FormFieldComponent from '../../Forms/FormFieldComponent';

import {
  maxFileSize,
  notNullOrUndefined,
  required,
  validateDuration,
} from '../../Forms/utils';
import { getDisabledScheduleValues } from '../../Schedule/utils';

import {
  CHECKBOX,
  DATE_PICKER,
  IMAGE_UPLOAD,
  REACT_SELECT_INPUT,
  STYLED_TEXT,
  TIME_PICKER,
} from '../../Forms/fieldTypes';

const EventForm = (props) => {
  const { t } = useTransl();
  const {
    isCreate,
    onSubmit,
    formError,
    isLoading,
    isSkeletonDisabled,
    isAllowGuests,
    isCustomLocation,
    isLimitAttendees,
    buildings,
    buildingId,
    amenityId,
    startTime,
    endTime,
    amenities,
  } = props;

  const fieldStateProps = { isLoading, isSkeletonDisabled };

  return (
    <Box my="1rem" sx={{ overflowWrap: 'anywhere' }}>
      <form onSubmit={onSubmit} method="post" autoComplete="off">
        {formError && (
          <Typography color="secondary" variant="caption">
            {formError}
          </Typography>
        )}
        <FormFieldComponent
          fieldType={STYLED_TEXT}
          keyName="name"
          label={t('title.eventName')}
          validate={[notNullOrUndefined]}
          {...fieldStateProps}
        />
        <FormFieldComponent
          fieldType={STYLED_TEXT}
          keyName="organizer"
          label={t('title.organizer')}
          validate={[notNullOrUndefined]}
          {...fieldStateProps}
        />
        <FormFieldComponent
          fieldType={DATE_PICKER}
          keyName="startDate"
          label={t('title.startDate')}
          disabled={false}
          validate={[notNullOrUndefined]}
          {...fieldStateProps}
        />
        <Box display="flex" gap="1rem">
          <FormFieldComponent
            fieldType={TIME_PICKER}
            timeFormat="h:mm a"
            use12Hours
            isMobilePicker
            isClearable={false}
            keyName="startTime"
            label={t('title.startTime')}
            validate={[notNullOrUndefined]}
            disabled={false}
            getDisabledHours={() => {}}
            {...fieldStateProps}
          />
          <FormFieldComponent
            fieldType={TIME_PICKER}
            timeFormat="h:mm a"
            use12Hours
            isMobilePicker
            isClearable={false}
            keyName="endTime"
            label={t('title.endTime')}
            validate={[notNullOrUndefined, validateDuration]}
            disabled={false}
            getDisabledHours={() =>
              getDisabledScheduleValues('hour', 'endTime', startTime, endTime)
            }
            getDisabledMinutes={(selectedHour) =>
              getDisabledScheduleValues(
                'minute',
                'endTime',
                startTime,
                endTime,
                selectedHour,
              )
            }
            {...fieldStateProps}
          />
        </Box>
        <FormFieldComponent
          fieldType={REACT_SELECT_INPUT}
          keyName="buildingId"
          options={buildings}
          disabled={!isCreate}
          label={t('title.building')}
          validate={[required]}
          {...fieldStateProps}
        />

        {buildingId && (
          <FormFieldComponent
            fieldType={REACT_SELECT_INPUT}
            keyName="amenityId"
            options={amenities}
            label={t('title.location')}
            placeholder={t('title.chooseAmenity')}
            disabled={isCustomLocation || !buildingId}
            validate={isCustomLocation ? [] : [required]}
            {...fieldStateProps}
          />
        )}

        {buildingId && (
          <>
            <FormFieldComponent
              fieldType={CHECKBOX}
              keyName="customLocationCheckBox"
              label={t('title.customLocation')}
              validate={[notNullOrUndefined]}
              {...fieldStateProps}
            />
            {isCustomLocation && (
              <FormFieldComponent
                fieldType={STYLED_TEXT}
                keyName="locationDescription"
                label={t('title.customLocation')}
                validate={[required]}
                disabled={amenityId}
                placeholder={t('title.enterCustomLocation')}
                {...fieldStateProps}
              />
            )}
          </>
        )}

        <FormFieldComponent
          fieldType={STYLED_TEXT}
          keyName="description"
          minRows={5}
          multiline
          label={t('title.eventDescription')}
          validate={[notNullOrUndefined]}
          placeholder={t('title.enterEventDescription')}
          {...fieldStateProps}
        />
        <FormFieldComponent
          fieldType={REACT_SELECT_INPUT}
          keyName="category"
          options={getEventsCategories()}
          label={t('title.category')}
          sortAlphabetically={false}
          placeholder={t('title.selectCategory')}
          validate={[required]}
          {...fieldStateProps}
        />

        <Box width="20%">
          <FormFieldComponent
            fieldType={IMAGE_UPLOAD}
            keyName="image"
            label={t('title.image')}
            validate={[maxFileSize]}
            {...fieldStateProps}
          />
        </Box>
        <FormFieldComponent
          fieldType={CHECKBOX}
          keyName="allowGuests"
          label={t('title.allowGuests')}
          validate={[notNullOrUndefined]}
          {...fieldStateProps}
        />

        <Box width="15%">
          <FormFieldComponent
            fieldType={STYLED_TEXT}
            keyName="guestsPerResidentLimit"
            label={t('title.maxGuests')}
            validate={[notNullOrUndefined]}
            isHidden={!isAllowGuests}
            {...fieldStateProps}
          />
        </Box>

        <FormFieldComponent
          fieldType={CHECKBOX}
          keyName="limitAttendees"
          label={t('title.attendeesLimit')}
          validate={[notNullOrUndefined]}
          {...fieldStateProps}
        />

        <Box width="15%">
          <FormFieldComponent
            fieldType={STYLED_TEXT}
            keyName="capacity"
            label={t('title.maxAttendees')}
            validate={[notNullOrUndefined]}
            isHidden={!isLimitAttendees}
            {...fieldStateProps}
          />
        </Box>

        <Box display="flex">
          <Button
            sx={{ marginRight: '1rem' }}
            type="submit"
            variant="contained"
            color="primary"
            disableElevation
          >
            {t('actions.submit')}
          </Button>
          <BackButtonComponent text={t('actions.cancel')} />
        </Box>
      </form>
    </Box>
  );
};

EventForm.propTypes = {
  isCreate: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formError: PropTypes.string,
  isLoading: PropTypes.bool,
  isSkeletonDisabled: PropTypes.bool,
  isAllowGuests: PropTypes.bool,
  isCustomLocation: PropTypes.bool,
  isLimitAttendees: PropTypes.bool,
  buildings: PropTypes.instanceOf(Array).isRequired,
  buildingId: PropTypes.number,
  amenityId: PropTypes.number,
  startTime: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Object),
  ]),
  endTime: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Object),
  ]),
  amenities: PropTypes.instanceOf(Object),
};

export default EventForm;
